/**
 * WhatsAppSubNav
 *
 * Subnav horizontal compartido por todas las páginas de /whatsapp.
 * Tabs: Cuentas | Campañas | Mensajes guardados | Analytics.
 *
 * - El tab "Cuentas" muestra contador al lado del label.
 * - Si `accountsCount === 0` resalta un dot rojo en el tab Cuentas para
 *   empujar al usuario a vincular primero.
 */

import { Link, useLocation } from "react-router-dom";
import {
    Smartphone,
    Send,
    BookOpenText,
    BarChart3,
} from "lucide-react";

type TabKey = "accounts" | "campaigns" | "messages" | "analytics";

interface WhatsAppSubNavProps {
    active: TabKey;
    accountsCount?: number;
}

const TABS: {
    key: TabKey;
    label: string;
    path: string;
    icon: React.ReactNode;
}[] = [
    {
        key: "accounts",
        label: "Cuentas",
        path: "/whatsapp/accounts",
        icon: <Smartphone size={14} />,
    },
    {
        key: "campaigns",
        label: "Envíos",
        path: "/whatsapp",
        icon: <Send size={14} />,
    },
    {
        key: "messages",
        label: "Mensajes guardados",
        path: "/whatsapp/templates",
        icon: <BookOpenText size={14} />,
    },
    {
        key: "analytics",
        label: "Analytics",
        path: "/whatsapp/analytics",
        icon: <BarChart3 size={14} />,
    },
];

export default function WhatsAppSubNav({
    active,
    accountsCount,
}: WhatsAppSubNavProps) {
    const noAccounts = accountsCount === 0;

    return (
        <nav className="flex items-center gap-1 border-b border-slate-200 -mx-1">
            {TABS.map((tab) => {
                const isActive = active === tab.key;
                const showWarning = tab.key === "accounts" && noAccounts;
                const showCount =
                    tab.key === "accounts" &&
                    typeof accountsCount === "number" &&
                    accountsCount > 0;

                return (
                    <Link
                        key={tab.key}
                        to={tab.path}
                        className={`relative flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors ${
                            isActive
                                ? "text-slate-900"
                                : "text-slate-500 hover:text-slate-800"
                        }`}
                    >
                        {tab.icon}
                        {tab.label}
                        {showCount && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 min-w-[18px] text-center">
                                {accountsCount}
                            </span>
                        )}
                        {showWarning && (
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 ml-0.5" />
                        )}
                        {isActive && (
                            <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-slate-900 rounded-t-full" />
                        )}
                    </Link>
                );
            })}
        </nav>
    );
}

/* Aux para usar fuera de React Router si hace falta */
export { TABS as WHATSAPP_TABS };
export type WhatsAppTabKey = TabKey;

/* Re-export del hook que se usa abajo, así otros components pueden checkear el path actual */
export function useIsActiveTab(target: TabKey): boolean {
    const location = useLocation();
    const tab = TABS.find((t) => t.key === target);
    if (!tab) return false;
    return location.pathname === tab.path;
}
